import { useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { Plus, Search, AlertTriangle, Stethoscope, CalendarClock, PackageX, History, User, Download, FileText, ChevronUp, ChevronDown } from 'lucide-react-native';

import { Button, Card, EmptyState, SkeletonRows, StatTile, Select, type SelectOption } from '@/components/ui';
import { ProductosTable } from '@/components/inventario/ProductosTable';
import { HistorialStockSheet } from '@/components/stock/HistorialStockSheet';
import { ProductoSheet } from '@/components/inventario/ProductoSheet';
import { UsarInsumoSheet } from '@/components/inventario/UsarInsumoSheet';
import { IngresarStockSheet } from '@/components/inventario/IngresarStockSheet';
import { AvisarReposicionSheet } from '@/components/inventario/AvisarReposicionSheet';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { insumosInternosQuery, eliminarInsumoInterno } from '@/services/firebase/inventario';
import { salidasQuery, actualizarSalida } from '@/services/firebase/salidas';
import { areasQuery } from '@/services/firebase/areas';
import { exportHistorialUsoExcel, exportHistorialUsoPDF, type FilaHistorialUso } from '@/services/export';
import { useAuth, selectIsAdmin, selectIsDoctor } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { diasHasta, normalizar, formatDateTime } from '@/utils/format';
import type { InsumoInterno } from '@/types/domain';

type Filtro = 'todos' | 'bajo' | 'agotado' | 'vencen' | 'historial';

export function InsumosInternosScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const { data, loading } = useFirestoreCollection(insumosInternosQuery);
  const { data: salidas } = useFirestoreCollection(salidasQuery);
  const isAdmin = useAuth(selectIsAdmin);
  const isDoctor = useAuth(selectIsDoctor);
  const perfil = useAuth((s) => s.perfil);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filtro>('todos');
  const [editing, setEditing] = useState<InsumoInterno | null>(null);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [usarTarget, setUsarTarget] = useState<InsumoInterno | null>(null);
  const [ingresarTarget, setIngresarTarget] = useState<InsumoInterno | null>(null);
  const [avisarTarget, setAvisarTarget] = useState<InsumoInterno | null>(null);
  const [histTarget, setHistTarget] = useState<InsumoInterno | null>(null);

  const filtered = useMemo(() => {
    let arr = data;
    if (filter === 'bajo') arr = arr.filter((p) => p.stockBajo && p.stock > 0);
    else if (filter === 'agotado') arr = arr.filter((p) => p.stock === 0);
    else if (filter === 'vencen') {
      arr = arr.filter((p) => {
        const v = p.vencimiento?.toDate?.();
        const d = diasHasta(v ?? null);
        return d !== null && d <= 60;
      });
    }
    if (search.trim()) {
      const q = normalizar(search);
      arr = arr.filter(
        (p) =>
          normalizar(p.nombre).includes(q) ||
          normalizar(p.presentacion).includes(q) ||
          normalizar(p.categoria).includes(q),
      );
    }
    return arr;
  }, [data, search, filter]);

  // Historial de uso: solo salidas de insumos internos, más reciente primero.
  const salidasInsumos = useMemo(
    () => salidas.filter((s) => s.coleccion === 'insumos_internos'),
    [salidas],
  );

  const historialFiltrado = useMemo(() => {
    const q = normalizar(search);
    if (!q) return salidasInsumos;
    return salidasInsumos.filter(
      (s) =>
        normalizar(s.productoNombre).includes(q) ||
        normalizar(s.motivo).includes(q) ||
        normalizar(s.destinoNombre).includes(q) ||
        normalizar(s.autorNombre).includes(q),
    );
  }, [salidasInsumos, search]);

  const stats = useMemo(() => {
    const total = data.length;
    const bajos = data.filter((p) => p.stockBajo && p.stock > 0).length;
    const agotados = data.filter((p) => p.stock === 0).length;
    const venceran = data.filter((p) => {
      const v = p.vencimiento?.toDate?.();
      const d = diasHasta(v ?? null);
      return d !== null && d <= 60;
    }).length;
    return { total, bajos, agotados, venceran, usos: salidasInsumos.length };
  }, [data, salidasInsumos]);

  const rol = perfil?.rol ?? 'doctor';

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 18, paddingBottom: 80 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <StatTile
          label="Todos"
          value={String(stats.total)}
          accent="magenta"
          icon={<Stethoscope size={22} color={colors.magenta[700]} />}
          active={filter === 'todos'}
          onPress={() => setFilter('todos')}
        />
        <StatTile
          label="Stock bajo"
          value={String(stats.bajos)}
          accent="warning"
          icon={<AlertTriangle size={22} color={colors.warning[600]} />}
          active={filter === 'bajo'}
          onPress={() => setFilter('bajo')}
        />
        <StatTile
          label="Agotados"
          value={String(stats.agotados)}
          accent="danger"
          icon={<PackageX size={22} color={colors.danger[600]} />}
          active={filter === 'agotado'}
          onPress={() => setFilter('agotado')}
        />
        <StatTile
          label="Vencen pronto"
          value={String(stats.venceran)}
          accent="warning"
          hint="≤ 60 días"
          icon={<CalendarClock size={22} color={colors.warning[600]} />}
          active={filter === 'vencen'}
          onPress={() => setFilter('vencen')}
        />
        <StatTile
          label="Historial de uso"
          value={String(stats.usos)}
          accent="magenta"
          hint="Qué se usó y quién"
          icon={<History size={22} color={colors.magenta[700]} />}
          active={filter === 'historial'}
          onPress={() => setFilter('historial')}
        />
      </View>

      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <View
            style={{
              flex: 1,
              minWidth: 220,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              backgroundColor: colors.crema[50],
              borderRadius: radius.full,
              paddingHorizontal: 14,
              height: 42,
              borderWidth: 1,
              borderColor: colors.ink[100],
            }}
          >
            <Search size={16} color={colors.ink[400]} />
            <TextInput
              placeholder={filter === 'historial' ? 'Buscar en historial…' : 'Buscar insumo…'}
              placeholderTextColor={colors.ink[400]}
              value={search}
              onChangeText={setSearch}
              style={{
                flex: 1,
                fontSize: 14,
                color: colors.ink[900],
                fontFamily: typography.body.fontFamily,
                // @ts-ignore web only
                outlineStyle: 'none',
              }}
            />
          </View>

          {isAdmin ? (
            <Button
              label="Nuevo insumo"
              variant="primary"
              size="md"
              iconLeft={<Plus size={16} color={colors.white} />}
              onPress={() => {
                setEditing(null);
                setEditSheetOpen(true);
              }}
            />
          ) : null}
        </View>
      </Card>

      {filter === 'historial' ? (
        <HistorialUso salidas={historialFiltrado} hayBusqueda={!!search.trim()} wide={wide} />
      ) : loading ? (
        <SkeletonRows rows={5} />
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            illustration={search || filter !== 'todos' ? 'note' : 'shelf'}
            title={search || filter !== 'todos' ? 'Sin resultados' : 'Aún no hay insumos cargados'}
            message={
              search || filter !== 'todos'
                ? 'Probá con otro término o tocá «Todos» para quitar filtros.'
                : 'La administradora cargará los insumos para uso clínico.'
            }
          />
        </Card>
      ) : (
        <ProductosTable
          productos={filtered}
          tipo="interno"
          rol={rol}
          onEdit={
            isAdmin
              ? (p) => {
                  setEditing(p as InsumoInterno);
                  setEditSheetOpen(true);
                }
              : undefined
          }
          onUsar={isAdmin || isDoctor ? (p) => setUsarTarget(p as InsumoInterno) : undefined}
          onIngresarStock={isAdmin ? (p) => setIngresarTarget(p as InsumoInterno) : undefined}
          onAvisarReposicion={isDoctor ? (p) => setAvisarTarget(p as InsumoInterno) : undefined}
          onEliminar={isAdmin ? (p) => eliminarInsumoInterno(p.id) : undefined}
          onVerHistorial={(p) => setHistTarget(p as InsumoInterno)}
        />
      )}

      <HistorialStockSheet visible={!!histTarget} onClose={() => setHistTarget(null)} productoId={histTarget?.id ?? null} productoNombre={histTarget?.nombre ?? ''} />
      <ProductoSheet visible={editSheetOpen} onClose={() => setEditSheetOpen(false)} tipo="interno" producto={editing} />
      <UsarInsumoSheet visible={!!usarTarget} onClose={() => setUsarTarget(null)} insumo={usarTarget} />
      <IngresarStockSheet visible={!!ingresarTarget} onClose={() => setIngresarTarget(null)} producto={ingresarTarget} tipo="interno" />
      <AvisarReposicionSheet visible={!!avisarTarget} onClose={() => setAvisarTarget(null)} producto={avisarTarget} origen="interno" />
    </ScrollView>
  );
}

// ============================================================================
// Historial de uso — planilla: fecha, producto, cantidad, detalle, área, quién
// ============================================================================
type SortKey = 'fecha' | 'producto' | 'cantidad' | 'area' | 'quien';

const COLS: { key: SortKey | 'detalle'; label: string; width: number; flex: number; sortable: boolean; align?: 'center' }[] = [
  { key: 'fecha', label: 'Fecha', width: 140, flex: 1.4, sortable: true },
  { key: 'producto', label: 'Producto', width: 200, flex: 2.2, sortable: true },
  { key: 'cantidad', label: 'Cantidad', width: 90, flex: 0.9, sortable: true, align: 'center' },
  { key: 'detalle', label: 'Detalle', width: 200, flex: 2.2, sortable: false },
  { key: 'area', label: 'Área', width: 190, flex: 1.9, sortable: true },
  { key: 'quien', label: 'Quién', width: 130, flex: 1.3, sortable: true },
];
const TABLE_WIDTH = COLS.reduce((s, c) => s + c.width, 0);

function HistorialUso({ salidas, hayBusqueda, wide }: { salidas: any[]; hayBusqueda: boolean; wide: boolean }) {
  const { data: areasData } = useFirestoreCollection(areasQuery);
  const [asignadas, setAsignadas] = useState<Record<string, { destino: string; destinoNombre: string }>>({});
  const [sortBy, setSortBy] = useState<SortKey>('fecha');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [desdeStr, setDesdeStr] = useState('');
  const [hastaStr, setHastaStr] = useState('');

  const opcionesArea = useMemo<SelectOption[]>(() => {
    const dyn = (areasData as any[]).filter((a) => a.activa !== false);
    return dyn.length > 0 ? dyn.map((a) => ({ value: a.slug, label: a.nombre })) : [];
  }, [areasData]);

  const asignar = (id: string, slug: string) => {
    const nombre = opcionesArea.find((o) => o.value === slug)?.label ?? slug;
    setAsignadas((p) => ({ ...p, [id]: { destino: slug, destinoNombre: nombre } }));
    actualizarSalida(id, { destino: slug, destinoNombre: nombre }).catch(() => {
      setAsignadas((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
    });
  };

  const areaDe = (s: any): string => asignadas[s.id]?.destinoNombre ?? s.destinoNombre ?? '';
  // En PC las columnas usan flex (ocupan toda la fila); en celu ancho fijo + scroll.
  const colW = (i: number) => (wide ? { flex: COLS[i].flex } : { width: COLS[i].width });

  const toggleSort = (k: SortKey) => {
    if (sortBy === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(k);
      setSortDir(k === 'fecha' || k === 'cantidad' ? 'desc' : 'asc');
    }
  };

  const rows = useMemo(() => {
    const desde = desdeStr ? new Date(desdeStr) : null;
    const hasta = hastaStr
      ? (() => {
          const h = new Date(hastaStr);
          h.setHours(23, 59, 59, 999);
          return h;
        })()
      : null;
    let arr = salidas.filter((s) => {
      const f = s.fecha?.toDate?.();
      if (desde && f && f < desde) return false;
      if (hasta && f && f > hasta) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    arr = [...arr].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'fecha') cmp = (a.fecha?.toMillis?.() ?? 0) - (b.fecha?.toMillis?.() ?? 0);
      else if (sortBy === 'cantidad') cmp = (a.cantidad ?? 0) - (b.cantidad ?? 0);
      else if (sortBy === 'producto') cmp = String(a.productoNombre ?? '').localeCompare(String(b.productoNombre ?? ''));
      else if (sortBy === 'area') cmp = areaDe(a).localeCompare(areaDe(b));
      else if (sortBy === 'quien') cmp = String(a.autorNombre ?? '').localeCompare(String(b.autorNombre ?? ''));
      return cmp * dir;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salidas, desdeStr, hastaStr, sortBy, sortDir, asignadas]);

  const filasExport = (): FilaHistorialUso[] =>
    rows.map((s) => ({
      fecha: s.fecha?.toDate?.() ?? null,
      producto: s.productoNombre,
      cantidad: s.cantidad,
      detalle: s.motivo ?? '',
      area: areaDe(s),
      quien: s.autorNombre ?? '',
    }));

  const exportar = async (tipo: 'excel' | 'pdf') => {
    try {
      const ctx = {
        filas: filasExport(),
        desde: desdeStr ? new Date(desdeStr) : undefined,
        hasta: hastaStr ? new Date(hastaStr) : undefined,
      };
      if (tipo === 'excel') await exportHistorialUsoExcel(ctx);
      else await exportHistorialUsoPDF(ctx);
    } catch (e: any) {
      alert(e?.message ?? 'No se pudo exportar.');
    }
  };

  const totalUnidades = rows.reduce((s, x) => s + (x.cantidad ?? 0), 0);

  return (
    <Card padding={wide ? 18 : 14} style={{ gap: 14 }}>
      {/* Toolbar: fechas + export */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          {Platform.OS === 'web' ? (
            <>
              <FechaInput label="Desde" value={desdeStr} onChange={setDesdeStr} />
              <FechaInput label="Hasta" value={hastaStr} onChange={setHastaStr} />
            </>
          ) : null}
          {desdeStr || hastaStr ? (
            <Pressable
              onPress={() => {
                setDesdeStr('');
                setHastaStr('');
              }}
              style={({ hovered }) => ({
                paddingHorizontal: 12,
                height: 40,
                borderRadius: radius.md,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? colors.crema[100] : colors.crema[50],
                borderWidth: 1,
                borderColor: colors.ink[200],
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Text style={{ fontSize: 12.5, fontWeight: typography.weights.bold, color: colors.ink[700] }}>Limpiar</Text>
            </Pressable>
          ) : null}
        </View>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Button label="Excel" variant="success" size="sm" iconLeft={<Download size={14} color={colors.white} />} onPress={() => exportar('excel')} />
          <Button label="PDF" variant="danger" size="sm" iconLeft={<FileText size={14} color={colors.white} />} onPress={() => exportar('pdf')} />
        </View>
      </View>

      <Text style={{ fontSize: 12, color: colors.ink[500] }}>
        {rows.length} {rows.length === 1 ? 'uso' : 'usos'} · {totalUnidades} unidades
      </Text>

      {rows.length === 0 ? (
        <EmptyState
          illustration="note"
          title={hayBusqueda || desdeStr || hastaStr ? 'Sin resultados' : 'Todavía no hay usos registrados'}
          message={
            hayBusqueda || desdeStr || hastaStr
              ? 'Probá con otro término o rango de fechas.'
              : 'Cada vez que uses un insumo con el botón «Usar», queda registrado acá: qué, cuánto, en qué área, quién y cuándo.'
          }
        />
      ) : (
        <TableScroller wide={wide}>
            {/* Header */}
            <View style={{ flexDirection: 'row', backgroundColor: colors.crema[50], borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, borderBottomWidth: 1, borderBottomColor: colors.ink[100] }}>
              {COLS.map((c, i) => {
                const activa = c.sortable && sortBy === c.key;
                return (
                  <Pressable
                    key={c.key}
                    onPress={c.sortable ? () => toggleSort(c.key as SortKey) : undefined}
                    style={{
                      ...colW(i),
                      paddingVertical: 11,
                      paddingHorizontal: 12,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: c.align === 'center' ? 'center' : 'flex-start',
                      gap: 4,
                      // @ts-ignore web only
                      cursor: c.sortable ? 'pointer' : 'default',
                    }}
                  >
                    <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: activa ? colors.magenta[700] : colors.ink[500], letterSpacing: 0.8, textTransform: 'uppercase' }}>
                      {c.label}
                    </Text>
                    {activa ? (
                      sortDir === 'asc' ? (
                        <ChevronUp size={13} color={colors.magenta[700]} />
                      ) : (
                        <ChevronDown size={13} color={colors.magenta[700]} />
                      )
                    ) : null}
                  </Pressable>
                );
              })}
            </View>
            {/* Filas */}
            {rows.map((s, idx) => {
              const f = s.fecha?.toDate?.();
              const area = areaDe(s);
              const sinArea = !area || area === '—';
              return (
                <View
                  key={s.id}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: idx % 2 === 1 ? 'rgba(250,247,244,0.6)' : colors.white,
                    borderBottomWidth: idx === rows.length - 1 ? 0 : 1,
                    borderBottomColor: colors.ink[100],
                  }}
                >
                  {/* Fecha */}
                  <View style={{ ...colW(0), paddingVertical: 10, paddingHorizontal: 12 }}>
                    <Text style={{ fontSize: 12.5, color: colors.ink[800] }}>{f ? formatDateTime(f) : '—'}</Text>
                  </View>
                  {/* Producto */}
                  <View style={{ ...colW(1), paddingVertical: 10, paddingHorizontal: 12 }}>
                    <Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: colors.ink[900] }} numberOfLines={2}>
                      {s.productoNombre}
                    </Text>
                  </View>
                  {/* Cantidad */}
                  <View style={{ ...colW(2), paddingVertical: 10, paddingHorizontal: 12, alignItems: 'center' }}>
                    <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 16, fontWeight: typography.weights.bold, color: colors.magenta[700] }}>
                      ×{s.cantidad}
                    </Text>
                  </View>
                  {/* Detalle */}
                  <View style={{ ...colW(3), paddingVertical: 10, paddingHorizontal: 12 }}>
                    <Text style={{ fontSize: 12.5, color: colors.ink[600], fontStyle: s.motivo ? 'italic' : 'normal' }} numberOfLines={2}>
                      {s.motivo || '—'}
                    </Text>
                  </View>
                  {/* Área */}
                  <View style={{ ...colW(4), paddingVertical: 8, paddingHorizontal: 12 }}>
                    {sinArea ? (
                      <Select value="" onChange={(v) => v && asignar(s.id, v as string)} options={[{ value: '', label: 'Asignar área…' }, ...opcionesArea]} />
                    ) : (
                      <Text style={{ fontSize: 12.5, color: colors.ink[800], fontWeight: typography.weights.semibold }} numberOfLines={1}>
                        {area}
                      </Text>
                    )}
                  </View>
                  {/* Quién */}
                  <View style={{ ...colW(5), paddingVertical: 10, paddingHorizontal: 12, flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                    <User size={12} color={colors.ink[400]} />
                    <Text style={{ fontSize: 12, color: colors.ink[700] }} numberOfLines={1}>
                      {s.autorNombre ?? '—'}
                    </Text>
                  </View>
                </View>
              );
            })}
        </TableScroller>
      )}
    </Card>
  );
}

// En PC: ocupa todo el ancho. En celu: scroll horizontal con ancho mínimo.
function TableScroller({ wide, children }: { wide: boolean; children: React.ReactNode }) {
  if (wide) return <View style={{ width: '100%' }}>{children}</View>;
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator style={{ marginHorizontal: -4 }}>
      <View style={{ minWidth: TABLE_WIDTH }}>{children}</View>
    </ScrollView>
  );
}

function FechaInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <View style={{ gap: 4 }}>
      <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase' }}>
        {label}
      </Text>
      {/* @ts-ignore web-only native input */}
      <input
        type="date"
        value={value}
        onChange={(e: any) => onChange(e.target.value)}
        style={{
          height: 40,
          padding: '0 12px',
          borderRadius: 10,
          border: `1.5px solid ${colors.ink[200]}`,
          background: colors.white,
          color: colors.ink[900],
          fontSize: 13,
          fontFamily: 'inherit',
          outline: 'none',
          width: 140,
          boxSizing: 'border-box',
        }}
      />
    </View>
  );
}
